import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, RotateCcw, ArrowUpCircle, CalendarPlus, Coins } from "lucide-react";
import { Plan, Platform, SubscriptionStatus } from "@/types/enums";
import type { AdminSubscriptionItem } from "@/types/subscription";
import type { SubscriptionAction } from "./subscription-columns";

const BLOCKED_STATUSES = new Set([SubscriptionStatus.PENDING_PAYMENT, SubscriptionStatus.PENDING_PLAN_CHANGE]);

function getDisabledReason(subscription: AdminSubscriptionItem): string | null {
  if (BLOCKED_STATUSES.has(subscription.status)) return "비활성 구독";
  return null;
}

function getRefundDisabledReason(
  subscription: AdminSubscriptionItem,
  baseReason: string | null,
): string | null {
  if (baseReason) return baseReason;
  if (subscription.plan === Plan.FREE) return "무료 플랜";
  return null;
}

function getRefundHint(subscription: AdminSubscriptionItem): string | null {
  if (subscription.plan === Plan.FREE) return null;
  if (subscription.platform && subscription.platform !== Platform.TOSS) {
    return "FREE 전환만";
  }
  return null;
}

interface SubscriptionActionsProps {
  subscription: AdminSubscriptionItem;
  onAction: (subscription: AdminSubscriptionItem, action: SubscriptionAction) => void;
}

const EXTENDABLE_PLANS = new Set([Plan.PRO_TRIAL, Plan.STUDENT_PASS]);
const BILLED_PLANS = new Set([Plan.PRO, Plan.PLUS, Plan.PREMIUM]);

function isExtendableSubscription(subscription: AdminSubscriptionItem): boolean {
  if (EXTENDABLE_PLANS.has(subscription.plan)) return true;
  // 모든 결제 플랫폼 — 서버가 platform 별 외부 API 호출로 연장.
  if (BILLED_PLANS.has(subscription.plan)) return true;
  return false;
}

export function SubscriptionActions({ subscription, onAction }: SubscriptionActionsProps) {
  const disabledReason = getDisabledReason(subscription);
  const isNotActive = disabledReason !== null;
  const isExtendable = isExtendableSubscription(subscription);
  const refundDisabledReason = getRefundDisabledReason(subscription, disabledReason);
  const isRefundDisabled = refundDisabledReason !== null;
  const refundHint = getRefundHint(subscription);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 min-h-[44px] w-8 min-w-[44px] p-0 md:h-8 md:min-h-0 md:w-8 md:min-w-0 data-[state=open]:bg-muted-foreground/20 data-[state=open]:text-foreground data-[state=open]:ring-1 data-[state=open]:ring-border"
          aria-label="Open subscription actions menu"
        >
          <span className="sr-only">메뉴 열기</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {isExtendable ? (
          <DropdownMenuItem
            onClick={() => onAction(subscription, "extend")}
            disabled={isNotActive}
            className="cursor-pointer"
          >
            <CalendarPlus className="mr-1 h-4 w-4" />
            연장
            {isNotActive && <span className="text-muted-foreground ml-auto text-xs">{disabledReason}</span>}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={() => onAction(subscription, "upgrade")}
            disabled={isNotActive}
            className="cursor-pointer"
          >
            <ArrowUpCircle className="mr-1 h-4 w-4" />
            업그레이드
            {isNotActive && <span className="text-muted-foreground ml-auto text-xs">{disabledReason}</span>}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => onAction(subscription, "grantCredits")}
          className="cursor-pointer"
        >
          <Coins className="mr-1 h-4 w-4" />
          크레딧 지급
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onAction(subscription, "refund")}
          disabled={isRefundDisabled}
          className="text-destructive focus:text-destructive cursor-pointer"
        >
          <RotateCcw className="text-destructive mr-1 h-4 w-4" />
          환불
          {(isRefundDisabled || refundHint) && (
            <span className="text-muted-foreground ml-auto text-xs">
              {isRefundDisabled ? refundDisabledReason : refundHint}
            </span>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
